import { useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import { GET_MEASUREMENTS_AND_CALCULATED_DIMENSIONS } from './gql';
import { getType } from './useEntities';

type CalcDimType = { kindWrapper?: { kind?: string }; typeNameWrapper?: { name?: string } } | string | undefined;

const parseCalcDimType = (type: CalcDimType): string | undefined => {
  if (type && typeof type === 'object') {
    if (type.kindWrapper) {
      return type.kindWrapper.kind;
    }
    if (type.typeNameWrapper) {
      return getType({ name: type.typeNameWrapper.name }, false);
    }
  }
  return typeof type === 'string' ? type : undefined;
};

export const useCalcDims = (options?) => {
  const queryResult = useGqlQuery<{
    findMeasurementsByAccountId: { measurement; createdByUserId; updatedByUserId; id }[];
    findCalculatedDimensionsByAccountId: { calculatedDimension; createdByUserId; updatedByUserId; id }[];
  }>({
    queryKey: [QueryKey.MeasurementsAndCalculatedDimensions],
    query: GET_MEASUREMENTS_AND_CALCULATED_DIMENSIONS,
    select: (_, data) => data,
    ...options,
  });

  const data = useMemo(() => {
    if (!queryResult.data) {
      return { all: [], visible: [], asObject: {} };
    }

    const calculatedDimensionsData = queryResult.data.findCalculatedDimensionsByAccountId || [];

    const calculatedDimensionsAll = calculatedDimensionsData.map(v => {
      const { calculatedDimension, ...rest } = v;
      return {
        ...calculatedDimension,
        ...rest,
        // Handle the nested type structure for calculated dimensions
        type: parseCalcDimType(calculatedDimension.type),
        builtin: calculatedDimension.builtin ?? false,
        hidden: calculatedDimension.hidden ?? false,
        createdByUserId: v.createdByUserId,
        updatedByUserId: v.updatedByUserId,
        id: v.id,
      };
    });

    return {
      all: calculatedDimensionsAll,
      visible: calculatedDimensionsAll.filter(({ hidden }) => !hidden),
      asObject: calculatedDimensionsAll.reduce((acc, item) => {
        const { systemName, ...rest } = item;
        acc[systemName] = rest;
        return acc;
      }, {}),
    };
  }, [queryResult.data]);

  return { ...queryResult, data };
};
